/**
 * ESG — публичная страница.
 * Весь контент читается из БД (таблица esg_content) через admin-db-proxy.
 * Статического контента в коде нет — только fallback-defaults пока БД пустая.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import {
  Download, Leaf, Users, Shield, BarChart3, Award,
  CheckCircle2, Factory, Recycle, ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { getPagePath } from '@/lib/localizedRoutes';
import {
  DEFAULT_PILLARS, DEFAULT_METRICS, DEFAULT_PRODUCTION,
  DEFAULT_QUALITY, DEFAULT_TEAM, DEFAULT_CERTIFICATES,
  PillarsBlock, MetricsBlock, ProductionBlock,
  QualityBlock, TeamBlock, CertificatesBlock,
  Lang,
} from '@/lib/esg-defaults';
import {
  getCachedEsgData, fetchEsgData, subscribeToCache,
} from '@/lib/globalDataCache';

// ─── Icon map ──────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  check: <CheckCircle2 className="w-5 h-5" />,
  award: <Award className="w-5 h-5" />,
  leaf: <Leaf className="w-5 h-5" />,
  shield: <Shield className="w-5 h-5" />,
  bar: <BarChart3 className="w-5 h-5" />,
  factory: <Factory className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  recycle: <Recycle className="w-5 h-5" />,
};

// ─── Animated progress bar ────────────────────────────────────────────────────

function AnimatedBar({ percent }: { percent: number }) {
  return (
    <div className="h-0.5 bg-white/5">
      <motion.div
        className="h-full bg-gradient-to-r from-[#B08A5A] to-[#D4A96A]"
        initial={{ width: 0 }}
        whileInView={{ width: `${percent}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}

// ─── Quality accordion item ────────────────────────────────────────────────────

function AccordionItem({ title, description }: { title: string; description: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-white/8 bg-white/[0.02] hover:border-[#B08A5A]/20 transition-all duration-300 cursor-pointer"
      onClick={() => setOpen(v => !v)}
    >
      <div className="flex items-center justify-between px-5 py-4">
        <p className="text-white/80 text-sm font-semibold pr-4">{title}</p>
        <ChevronDown className={cn('w-4 h-4 text-[#B08A5A] flex-shrink-0 transition-transform duration-300', open && 'rotate-180')} />
      </div>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-white/50 text-sm leading-relaxed">{description}</p>
        </div>
      )}
    </div>
  );
}

// ─── Inner component (key=lang guarantees full remount on language switch) ─────
//
// Splitting into ESGInner + ESG wrapper is the simplest, most reliable way to
// ensure that ALL state (pillars, metrics, etc.) is always in sync with the
// selected language:
//   - React destroys ESGInner and creates a fresh instance on each lang change
//   - No race conditions possible — old instance is gone before new one mounts
//   - No stale state from previous language can bleed through

interface ESGInnerProps { lang: Lang; seoEnabled?: boolean }

const ESGInner: React.FC<ESGInnerProps> = ({ lang, seoEnabled }) => {
  // Use lang from prop directly — no useLanguage() here.
  // The parent ESG wrapper uses key={lang} to remount ESGInner on every lang
  // change, so lang is always stable and correct within this instance.
  const esgHero = usePageHero({
    pageKey: 'esg',
    lang,
    fallbacks: {
      eyebrow: lang === 'uz' ? "Mas'uliyatli ishlab chiqarish" : lang === 'en' ? 'Responsible Manufacturing' : 'Ответственное производство',
      title: 'ESG.',
      subtitle: lang === 'uz' ? "Ekologiya. Ijtimoiy mas'uliyat. Boshqaruv." : lang === 'en' ? 'Environment. Social. Governance.' : 'Экология. Социальная ответственность. Управление.',
    },
  });

  // ─── State ─────────────────────────────────────────────────────────────────
  // Initialize from the global in-memory/localStorage cache SYNCHRONOUSLY.
  // getCachedEsgData() reads from the module-level cache which is pre-populated
  // from localStorage before the first render — so on repeat visits the data
  // is available instantly with zero network round-trip.
  //
  // On first-ever visit (no localStorage): defaults are shown immediately,
  // then replaced when fetchEsgData() resolves (already in-flight from
  // prefetchAllData() called at app start).
  const getInitialData = () => {
    const cached = getCachedEsgData(lang);
    if (cached) return cached;
    return {
      pillars:    DEFAULT_PILLARS[lang],
      metrics:    DEFAULT_METRICS[lang],
      production: DEFAULT_PRODUCTION[lang],
      quality:    DEFAULT_QUALITY[lang],
      team:       DEFAULT_TEAM[lang],
      certs:      DEFAULT_CERTIFICATES[lang],
    };
  };
  const initial = getInitialData();
  const [pillars,    setPillars]    = useState<PillarsBlock>(initial.pillars);
  const [metrics,    setMetrics]    = useState<MetricsBlock>(initial.metrics);
  const [production, setProduction] = useState<ProductionBlock>(initial.production);
  const [quality,    setQuality]    = useState<QualityBlock>(initial.quality);
  const [team,       setTeam]       = useState<TeamBlock>(initial.team);
  const [certs,      setCerts]      = useState<CertificatesBlock>(initial.certs);

  // ─── Sync with cache ──────────────────────────────────────────────────────
  // If fetchEsgData was already called (by prefetchAllData at app start),
  // the promise may resolve very quickly. We await it here and update state.
  // If the cache was already fresh, fetchEsgData returns immediately.
  // subscribeToCache ensures we pick up any update that arrives after mount.
  useEffect(() => {
    let cancelled = false;

    const apply = (data: { pillars: PillarsBlock; metrics: MetricsBlock; production: ProductionBlock; quality: QualityBlock; team: TeamBlock; certs: CertificatesBlock }) => {
      if (cancelled) return;
      setPillars(data.pillars);
      setMetrics(data.metrics);
      setProduction(data.production);
      setQuality(data.quality);
      setTeam(data.team);
      setCerts(data.certs);
    };

    // Fetch (will resolve from in-memory cache if already loaded, otherwise fetches)
    fetchEsgData(lang).then(apply).catch(() => { /* keep defaults */ });

    // Also subscribe to cache updates (e.g. if prefetch resolves after mount)
    const unsubscribe = subscribeToCache(() => {
      const cached = getCachedEsgData(lang);
      if (cached) apply(cached);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // ESGInner is remounted on lang change (key={lang}), so lang is stable here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // headerVariants — left-to-right entrance, matching /news page behaviour
  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
  };

  const PILLAR_STYLES = [
    { colorClass: 'text-emerald-400', borderClass: 'border-emerald-400/20', bgClass: 'bg-emerald-400/5' },
    { colorClass: 'text-blue-400', borderClass: 'border-blue-400/20', bgClass: 'bg-blue-400/5' },
    { colorClass: 'text-amber-400', borderClass: 'border-amber-400/20', bgClass: 'bg-amber-400/5' },
  ];

  const pillarCards = pillars ? [
    { ...pillars.env, style: PILLAR_STYLES[0] },
    { ...pillars.soc, style: PILLAR_STYLES[1] },
    { ...pillars.gov, style: PILLAR_STYLES[2] },
  ] : [];

  return (
    <>
      <SEO
        enabled={seoEnabled}
        title={lang === 'uz' ? 'SARGO | ESG - Barqaror rivojlanish va mas’uliyat' : lang === 'en' ? 'SARGO | ESG - Sustainability and Responsibility' : 'SARGO | ESG - Устойчивое развитие и ответственность'}
        description={lang === 'uz'
          ? "SARGO ESG yondashuvi: ekologik mas'uliyat, sifat nazorati, barqaror ishlab chiqarish va korporativ standartlar."
          : lang === 'en'
            ? 'SARGO ESG approach: environmental responsibility, quality control, sustainable production and corporate standards.'
            : 'ESG-подход SARGO: экологическая ответственность, контроль качества, устойчивое производство и корпоративные стандарты.'}
        canonical={getPagePath('esg', lang)}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: lang === 'uz' ? 'Bosh sahifa' : lang === 'en' ? 'Home' : 'Главная', url: `/${lang}` },
          { name: 'ESG', url: getPagePath('esg', lang) },
        ])}
      />

      <motion.div
        key="esg-page"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-background"
        style={{ overflowX: 'clip' }}
      >
        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <div className="relative w-full bg-background z-10">
          <div className="grid-cells opacity-10" />
          <div className="relative z-10">
            <section className="pt-24 md:pt-32 pb-0 lg:pb-16 relative">
              <div className="grid-cells opacity-50" />
              <div className="container-custom relative z-10">
                <div className="space-y-4 lg:space-y-6">
                  {esgHero.eyebrow?.trim() && (
                    <motion.div
                      custom={0}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={headerVariants}
                      className="flex items-center gap-2 lg:gap-3"
                    >
                      <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-[#B08A5A] shadow-[0_0_10px_rgba(176,138,90,1)]" />
                      <p className="text-[#B08A5A] font-black uppercase tracking-[0.4em] text-[10px]">{esgHero.eyebrow}</p>
                    </motion.div>
                  )}
                  {esgHero.title?.trim() && (
                    <motion.h1
                      custom={1}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={headerVariants}
                      className="text-white font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]"
                    >
                      {esgHero.title.endsWith('.') ? esgHero.title.slice(0, -1) : esgHero.title}
                      <span className="text-[#B08A5A]">.</span>
                    </motion.h1>
                  )}
                  {esgHero.subtitle?.trim() && (
                    <motion.p
                      custom={2}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={headerVariants}
                      className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider"
                    >
                      {esgHero.subtitle}
                    </motion.p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* ── ESG PILLARS ─────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(5rem, 8vh, 10rem)', paddingBottom: 'clamp(4rem, 6vh, 8rem)' }}>
          <div className="grid-cells opacity-30" />
          <div className="container-custom relative z-10">
            {pillars && (
              <motion.div variants={itemVariants} className="mb-12 lg:mb-16">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-[#B08A5A]" />
                  <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{pillars.eyebrow}</span>
                </div>
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.75rem, 4vw, 4rem)' }}>
                  {pillars.heading}
                </h2>
              </motion.div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              {pillarCards.map((card, i) => (
                <motion.div key={i} variants={itemVariants}
                  className={cn('relative p-6 lg:p-8 border group transition-all duration-300 hover:border-opacity-60', card.style.borderClass, card.style.bgClass)}
                >
                  <div className={cn('absolute top-4 right-4 font-black leading-none select-none pointer-events-none', card.style.colorClass)}
                    style={{ fontSize: 'clamp(4rem, 8vw, 8rem)', opacity: 0.04 }}>
                    {card.letter}
                  </div>
                  <div className={cn('w-10 h-10 flex items-center justify-center mb-6 text-2xl font-black', card.style.colorClass)}>
                    {card.letter}
                  </div>
                  <div className="space-y-1 mb-4">
                    <p className={cn('font-black uppercase tracking-[0.3em] text-[10px]', card.style.colorClass)}>
                      {i === 0 ? 'Environment' : i === 1 ? 'Social' : 'Governance'}
                    </p>
                    <h3 className="text-white font-black uppercase tracking-tight" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{card.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── METRICS ─────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 6vh, 8rem)', paddingBottom: 'clamp(4rem, 6vh, 8rem)' }}>
          <div className="grid-cells opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B08A5A]/30 to-transparent" />
          <div className="container-custom relative z-10">
            {metrics && (
              <motion.div variants={itemVariants} className="mb-12 lg:mb-16">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-[#B08A5A]" />
                  <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{metrics.eyebrow}</span>
                </div>
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.75rem, 4vw, 4rem)' }}>
                  {metrics.heading}
                </h2>
              </motion.div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {metrics?.items.map((m, i) => (
                <motion.div key={i} variants={itemVariants}
                  className="relative border border-white/8 bg-white/[0.02] p-5 lg:p-7 group hover:border-[#B08A5A]/30 hover:bg-white/[0.04] transition-all duration-300"
                >
                  <div className="flex items-center gap-2 text-[#B08A5A] mb-3">
                    {ICON_MAP[m.icon] ?? <CheckCircle2 className="w-5 h-5" />}
                  </div>
                  <div className="font-black text-white tracking-tighter leading-none mb-2" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
                    {m.value}
                  </div>
                  <p className="text-white/40 text-xs font-semibold uppercase tracking-wider leading-tight">{m.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── ENVIRONMENTAL PRODUCTION ─────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 6vh, 8rem)', paddingBottom: 'clamp(4rem, 6vh, 8rem)' }}>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
          <div className="container-custom relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {production && (
                <motion.div variants={itemVariants} className="space-y-6 lg:space-y-8">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-px bg-[#B08A5A]" />
                      <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{production.eyebrow}</span>
                    </div>
                    <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 3.5rem)' }}>
                      {production.heading}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {production.advantages.map((adv, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Recycle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#B08A5A]" />
                        <p className="text-white/60 text-sm leading-relaxed">{adv}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
              <motion.div variants={itemVariants} className="space-y-3">
                {production?.bars.map((bar, i) => (
                  <div key={i} className="border border-white/8 bg-white/[0.02] p-4 lg:p-5">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-xs font-semibold uppercase tracking-wider">{bar.label}</span>
                      <span className="text-[#B08A5A] font-black text-sm">{bar.value}</span>
                    </div>
                    <AnimatedBar percent={bar.percent} />
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── QUALITY CONTROL ─────────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 6vh, 8rem)', paddingBottom: 'clamp(4rem, 6vh, 8rem)' }}>
          <div className="grid-cells opacity-25" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B08A5A]/20 to-transparent" />
          <div className="container-custom relative z-10">
            {quality && (
              <motion.div variants={itemVariants} className="mb-12 lg:mb-16">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-[#B08A5A]" />
                  <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{quality.eyebrow}</span>
                </div>
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.75rem, 4vw, 4rem)' }}>
                  {quality.heading}
                </h2>
              </motion.div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
              {quality?.items.map((item, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <AccordionItem title={item.title} description={item.description} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TEAM AND STANDARDS ──────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 6vh, 8rem)', paddingBottom: 'clamp(4rem, 6vh, 8rem)' }}>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
          <div className="container-custom relative z-10">
            {team && (
              <motion.div variants={itemVariants} className="mb-12 lg:mb-16">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-[#B08A5A]" />
                  <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{team.eyebrow}</span>
                </div>
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.75rem, 4vw, 4rem)' }}>
                  {team.heading}
                </h2>
              </motion.div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {team?.cards.map((card, i) => (
                <motion.div key={i} variants={itemVariants} className="relative group">
                  <div className="absolute top-0 right-0 font-black leading-none select-none pointer-events-none text-[#B08A5A]"
                    style={{ fontSize: 'clamp(5rem, 10vw, 12rem)', opacity: 0.04, lineHeight: 1 }}>
                    {card.number}
                  </div>
                  <div className="relative z-10 space-y-4">
                    <div className="font-black text-[#B08A5A] text-xs tracking-[0.3em] uppercase">{card.number}</div>
                    <h3 className="font-black uppercase tracking-tight text-white" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.5rem)' }}>
                      {card.title}
                    </h3>
                    <div className="w-8 h-px bg-[#B08A5A]/40 group-hover:w-16 transition-all duration-500" />
                    <p className="text-white/50 text-sm leading-relaxed">{card.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CERTIFICATES ────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 6vh, 8rem)', paddingBottom: 'clamp(5rem, 8vh, 10rem)' }}>
          <div className="grid-cells opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B08A5A]/30 to-transparent" />
          <div className="container-custom relative z-10">
            {certs && (
              <motion.div variants={itemVariants} className="mb-12 lg:mb-16">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-[#B08A5A]" />
                  <span className="text-[#B08A5A] font-black uppercase tracking-[0.3em] text-[10px]">{certs.eyebrow}</span>
                </div>
                <h2 className="font-black uppercase tracking-tighter leading-[0.95] text-white" style={{ fontSize: 'clamp(1.75rem, 4vw, 4rem)' }}>
                  {certs.heading}
                </h2>
              </motion.div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {certs?.items.map((cert, i) => (
                <motion.div key={cert.id || i} variants={itemVariants}
                  className="group border border-white/8 bg-white/[0.02] hover:border-[#B08A5A]/30 hover:bg-white/[0.05] transition-all duration-300 flex flex-col"
                >
                  <div className="relative overflow-hidden bg-white/5" style={{ aspectRatio: '3/4' }}>
                    {cert.previewUrl ? (
                      <img src={cert.previewUrl} alt={cert.title} loading="lazy"
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Award className="w-10 h-10 text-white/10" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-[#13100B]/80 backdrop-blur-sm px-2 py-0.5">
                      <span className="text-[#B08A5A] font-black text-[10px] tracking-widest">{cert.year}</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#13100B] via-transparent to-transparent opacity-60" />
                  </div>
                  <div className="flex flex-col flex-1 p-4">
                    <p className="text-white/80 text-xs font-semibold leading-tight flex-1 mb-4">{cert.title}</p>
                    {cert.downloadUrl ? (
                      <a href={cert.downloadUrl} download target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 py-2 px-3 border border-[#B08A5A]/30 text-[#B08A5A] hover:bg-[#B08A5A]/10 hover:border-[#B08A5A]/60 transition-all duration-200 text-[10px] font-black uppercase tracking-[0.2em]"
                      >
                        <Download className="w-3 h-3" />
                        {lang === 'uz' ? 'Yuklab olish' : lang === 'en' ? 'Download' : 'Скачать'}
                      </a>
                    ) : (
                      <div className="flex items-center justify-center gap-2 py-2 px-3 border border-white/10 text-white/20 text-[10px] font-black uppercase tracking-[0.2em]">
                        <Download className="w-3 h-3" />
                        {lang === 'uz' ? 'Yuklab olish' : lang === 'en' ? 'Download' : 'Скачать'}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

// ─── Public export — wrapper that remounts ESGInner on every lang change ──────
export const ESG: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { language } = useLanguage();
  const lang = language as Lang;
  return <ESGInner key={lang} lang={lang} seoEnabled={seoEnabled} />;
};

export default ESG;